import { useEffect, useMemo, useRef } from 'react';

export interface CommanderEntry {
  id: string;
  name: string;
  title: string;
  faction: string;
  flavor: string;
  description: string[];
}

export interface FactionEntry {
  id: string;
  name: string;
  color: number;
}

interface CommanderSelectModalProps {
  commanders: CommanderEntry[];
  factions: Record<string, FactionEntry>;
  currentId?: string;
  onSelect: (id: string | undefined) => void;
  onClose: () => void;
}

const OVERLAY_STYLE: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.65)',
  zIndex: 1000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
};

const MODAL_STYLE: React.CSSProperties = {
  backgroundColor: 'var(--panel-bg)',
  color: 'var(--text-primary)',
  border: '1px solid var(--panel-border)',
  borderRadius: 8,
  maxWidth: 900,
  width: '100%',
  maxHeight: '85vh',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
};

const HEADER_STYLE: React.CSSProperties = {
  padding: '20px 24px 12px',
  borderBottom: '1px solid var(--panel-border)',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const BODY_STYLE: React.CSSProperties = {
  padding: 20,
  overflowY: 'auto',
  flex: 1,
};

const FACTION_HEADER_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  margin: '16px 0 8px',
  paddingBottom: 4,
  borderBottom: '1px solid var(--panel-border)',
};

const GRID_STYLE: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
  gap: 12,
};

const CARD_STYLE: React.CSSProperties = {
  border: '2px solid var(--panel-border)',
  borderRadius: 8,
  padding: 12,
  cursor: 'pointer',
  transition: 'border-color 0.15s, box-shadow 0.15s',
  background: 'var(--panel-bg-raised)',
  textAlign: 'left',
};

const CARD_SELECTED_STYLE: React.CSSProperties = {
  ...CARD_STYLE,
  borderColor: 'var(--p1)',
  boxShadow: '0 0 0 2px rgba(104, 184, 232, 0.25)',
};

function numberToHex(color: number): string {
  return '#' + color.toString(16).padStart(6, '0');
}

export default function CommanderSelectModal({
  commanders,
  factions,
  currentId,
  onSelect,
  onClose,
}: CommanderSelectModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) onClose();
  };

  // Group commanders by faction, preserving the server's ordering.
  const byFaction = useMemo(() => {
    const groups: { faction: FactionEntry; commanders: CommanderEntry[] }[] =
      [];
    const seen = new Set<string>();
    for (const commander of commanders) {
      if (!seen.has(commander.faction)) {
        seen.add(commander.faction);
        groups.push({
          faction: factions[commander.faction] ?? {
            id: commander.faction,
            name: commander.faction,
            color: 0x888888,
          },
          commanders: [],
        });
      }
      groups
        .find((g) => g.faction.id === commander.faction)
        ?.commanders.push(commander);
    }
    return groups;
  }, [commanders, factions]);

  return (
    <div ref={overlayRef} style={OVERLAY_STYLE} onClick={handleOverlayClick}>
      <div style={MODAL_STYLE}>
        <div style={HEADER_STYLE}>
          <h2 className="title is-5 mb-0">Choose Commander</h2>
          <button
            className="delete is-medium"
            onClick={onClose}
            type="button"
            aria-label="Close"
          />
        </div>
        <div style={BODY_STYLE}>
          <div style={{ marginBottom: 12 }}>
            <div
              style={{
                ...CARD_STYLE,
                borderColor: currentId === undefined ? 'var(--p1)' : undefined,
                boxShadow:
                  currentId === undefined
                    ? '0 0 0 2px rgba(104, 184, 232, 0.25)'
                    : undefined,
              }}
              onClick={() => onSelect(undefined)}
            >
              <p style={{ fontWeight: 700, marginBottom: 2 }}>No Commander</p>
              <p
                style={{
                  fontSize: 12,
                  color: 'var(--text-secondary)',
                  marginBottom: 0,
                }}
              >
                Play without any passive modifiers.
              </p>
            </div>
          </div>

          {byFaction.map(({ faction, commanders: group }) => (
            <div key={faction.id}>
              <div style={FACTION_HEADER_STYLE}>
                <span
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: numberToHex(faction.color),
                    display: 'inline-block',
                  }}
                />
                <span style={{ fontWeight: 700 }}>{faction.name}</span>
              </div>
              <div style={GRID_STYLE}>
                {group.map((commander) => {
                  const isSelected = commander.id === currentId;
                  return (
                    <div
                      key={commander.id}
                      style={isSelected ? CARD_SELECTED_STYLE : CARD_STYLE}
                      onClick={() => onSelect(commander.id)}
                    >
                      <p style={{ fontWeight: 700, marginBottom: 1 }}>
                        {commander.name}
                      </p>
                      <p
                        style={{
                          fontSize: 12,
                          color: 'var(--text-secondary)',
                          marginBottom: 6,
                        }}
                      >
                        {commander.title}
                      </p>
                      <p
                        style={{
                          fontSize: 11,
                          fontStyle: 'italic',
                          color: 'var(--text-secondary)',
                          marginBottom: 6,
                          lineHeight: 1.35,
                        }}
                      >
                        {commander.flavor}
                      </p>
                      <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12 }}>
                        {commander.description.map((line) => (
                          <li key={line} style={{ lineHeight: 1.4 }}>
                            {line}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
